import React from 'react';
import Icon from 'src/components/common/Icon';
import {
    ORIGIN_HINTS,
    ORIGIN_LABELS,
} from 'src/redux/panel_transactions/panel_transactions.constants';
import {
    PanelPaymentStatus,
    PanelTransaction,
    PanelTransactionAgent,
} from 'src/redux/panel_transactions/panel_transactions.types';
import {
    EmptyState,
    StatusBadge,
    Table,
    TableWrapper,
} from './Transactions.styles';
import {
    PENDING_VALUE,
    buildTransactionsColumnLayout,
    formatAmount,
    formatCustomer,
    formatDateTime,
    formatOriginAgent,
    formatOriginAgentHint,
    formatPaymentMethod,
    formatPaymentStatus,
    formatRawStatus,
} from './Transactions.helpers';

export const TRANSACTIONS_TABLE_EMPTY_COPY = 'No hay transacciones para mostrar.';

interface TransactionsTableProps {
    transactions: PanelTransaction[];
    /**
     * Muestra la columna Cliente. Va en el listado de la cartera del comercial
     * (Estado B); cuando ya se buscó un cliente sobra, porque todas las filas
     * son de esa persona.
     */
    showCustomer?: boolean;
    /**
     * Muestra la columna Origen. Va con alcance global, donde el listado mezcla
     * ventas propias, de otros comerciales y compras que el cliente hizo por su
     * cuenta. Con alcance propio sobra, porque todas las filas son suyas.
     */
    showOrigin?: boolean;
    emptyCopy?: string;
}

const ORIGIN_COLUMN_HINT =
    'Quién originó la operación y qué comercial la hizo. Con alcance global el listado trae también las ventas de otros y las compras del propio cliente.';

const TYPE_LABELS: Record<PanelTransaction['type'], string> = {
    one_time: 'Pago único',
    subscription: 'Suscripción',
};

const STATUS_ICONS: Record<PanelPaymentStatus, string> = {
    successful: 'pcom-icon-circle-check-outline',
    in_process: 'pcom-icon-time',
    failed: 'pcom-icon-circle-close-outline',
};

/**
 * Columnas sin fuente de datos todavía. El texto explica por qué están vacías y
 * en qué entrega se llenan; viaja como tooltip de la celda.
 */
const PENDING_COLUMNS: { title: string; hint: string }[] = [
    {
        title: 'Activación',
        hint: 'El estado de activación del producto todavía no se registra.',
    },
    {
        title: 'Suscripción',
        hint: 'El estado de la suscripción todavía no se registra.',
    },
    {
        title: 'Factura',
        hint: 'El estado de facturación todavía no se registra.',
    },
];

/**
 * Celda de origen: de quién es la venta arriba, la categoría abajo.
 *
 * La persona va en la línea principal porque es el dato que cambia entre filas y
 * lo que un rol administrativo necesita —saber que una venta no es tuya no dice a
 * quién preguntarle—. La categoría baja a la segunda línea y solo aparece cuando
 * agrega algo: si no se sabe quién fue, ella misma es la línea principal.
 */
const OriginCell = ({
    origin,
    agent,
}: {
    origin: NonNullable<PanelTransaction['origin']>;
    agent: PanelTransactionAgent | null;
}) => {
    const agentLabel = formatOriginAgent(agent);

    return (
        <>
            <span
                className="cell-main"
                data-id="transactions-origin-who"
                title={agentLabel ? formatOriginAgentHint(agent) : ORIGIN_HINTS[origin]}
            >
                {agentLabel ?? ORIGIN_LABELS[origin]}
            </span>
            {agentLabel && (
                <span
                    className="cell-sub"
                    data-id="transactions-origin-kind"
                    title={ORIGIN_HINTS[origin]}
                >
                    {ORIGIN_LABELS[origin]}
                </span>
            )}
        </>
    );
};

/**
 * Tabla del listado de transacciones (nivel 1 de la pestaña). Solo lectura: las
 * filas todavía no llevan a un detalle.
 *
 * Cada celda apila el dato principal sobre su contexto (monto sobre método,
 * producto sobre tipo) para caber en una fila sin multiplicar columnas.
 *
 * Activación, Suscripción y Factura existen desde el principio para que la
 * tabla no cambie de forma cuando tengan datos; hoy muestran "—" con tooltip.
 */
const TransactionsTable = ({
    transactions,
    showCustomer = false,
    showOrigin = false,
    emptyCopy = TRANSACTIONS_TABLE_EMPTY_COPY,
}: TransactionsTableProps) => {
    if (!transactions.length) {
        return <EmptyState>{emptyCopy}</EmptyState>;
    }

    const { columns } = buildTransactionsColumnLayout({
        showCustomer,
        showOrigin,
    });

    return (
        <TableWrapper data-id="transactions-table">
            <Table>
                <colgroup>
                    {columns.map(({ key, width }) => (
                        <col key={key} style={{ width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        <th>Transacción / Fecha</th>
                        {showCustomer && <th>Cliente</th>}
                        {showOrigin && <th title={ORIGIN_COLUMN_HINT}>Origen</th>}
                        <th>Producto y tipo</th>
                        <th className="amount-cell">Monto y método</th>
                        <th className="payment-cell">Pago</th>
                        {PENDING_COLUMNS.map(({ title, hint }) => (
                            <th key={title} title={hint}>
                                {title}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {transactions.map((transaction) => (
                        <tr key={transaction.transaction_id}>
                            {/* La orden ES la transacción en este incremento, así
                                que repetir el número no aporta: la segunda línea
                                usa la fecha y el id de orden va en el tooltip. */}
                            <td
                                data-id="transactions-reference"
                                title={`Orden ORD-${transaction.order_id}`}
                            >
                                <span className="cell-main">
                                    TX-{transaction.transaction_id}
                                </span>
                                <span className="cell-sub">
                                    {formatDateTime(transaction.created)}
                                </span>
                            </td>
                            {showCustomer && (
                                <td
                                    className="customer-cell"
                                    data-id="transactions-customer"
                                    title={
                                        transaction.customer.name &&
                                        transaction.customer.email
                                            ? `${transaction.customer.name} · ${transaction.customer.email}`
                                            : formatCustomer(
                                                  transaction.customer.name,
                                                  transaction.customer.email,
                                              )
                                    }
                                >
                                    <span className="cell-main">
                                        {formatCustomer(
                                            transaction.customer.name,
                                            transaction.customer.email,
                                        )}
                                    </span>
                                    {/* Sin nombre la línea principal ya es el
                                        correo: repetirlo abajo sobra. */}
                                    {transaction.customer.name &&
                                        transaction.customer.email && (
                                            <span className="cell-sub">
                                                {transaction.customer.email}
                                            </span>
                                        )}
                                </td>
                            )}
                            {showOrigin && (
                                <td data-id="transactions-origin">
                                    {transaction.origin ? (
                                        <OriginCell
                                            origin={transaction.origin}
                                            agent={transaction.origin_agent}
                                        />
                                    ) : (
                                        PENDING_VALUE
                                    )}
                                </td>
                            )}
                            <td className="product-cell" data-id="transactions-product">
                                <span className="cell-main">
                                    {transaction.product_label ?? PENDING_VALUE}
                                </span>
                                <span className="cell-sub">
                                    {TYPE_LABELS[transaction.type]}
                                </span>
                            </td>
                            <td
                                className="amount-cell"
                                data-id="transactions-amount"
                                title={formatPaymentMethod(
                                    transaction.payment_gateway,
                                    transaction.payment_method,
                                )}
                            >
                                <span className="cell-main">
                                    {formatAmount(transaction.amount)}
                                </span>
                                <span className="cell-sub">
                                    {formatPaymentMethod(
                                        transaction.payment_gateway,
                                        transaction.payment_method,
                                    )}
                                </span>
                            </td>
                            <td className="payment-cell">
                                <StatusBadge
                                    data-id="transactions-payment-status"
                                    $status={transaction.payment_status}
                                    title={formatRawStatus(transaction.payment_status_raw)}
                                >
                                    {transaction.payment_status && (
                                        <Icon
                                            icon={STATUS_ICONS[transaction.payment_status]}
                                            size={12}
                                        />
                                    )}
                                    {formatPaymentStatus(transaction.payment_status)}
                                </StatusBadge>
                            </td>
                            {PENDING_COLUMNS.map(({ title, hint }) => (
                                <td key={title} className="pending-cell" title={hint}>
                                    {PENDING_VALUE}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </TableWrapper>
    );
};

export default TransactionsTable;
